/**
 * A row thumbnail of a generation's result with the print guide painted grey (owner,
 * 2026-10-03: a generations row never shows magenta). Thumbnail size only: the preview dialog
 * and the editor keep the real image.
 */

/** What a guide pixel becomes. */
export const GUIDE_GREY = [0xd9, 0xd9, 0xd9] as const;

/** The cloud marker's idea of magenta: red and blue well above green, and close together. */
const MIN_SCORE = 42;
const MAX_RED_BLUE_GAP = 70;

/** Paint every guide pixel of an RGBA buffer grey, in place. Alpha stays. */
export function paintGuideGrey(rgba: Uint8ClampedArray): void {
  for (let i = 0; i < rgba.length; i += 4) {
    const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
    if (Math.min(r, b) - g < MIN_SCORE || Math.abs(r - b) > MAX_RED_BLUE_GAP) continue;
    rgba.set(GUIDE_GREY, i);
  }
}

/** The 38px thumbnail slot at up to 3x. */
const THUMB_PX = 114;

function greyDataUrl(img: HTMLImageElement): string {
  const scale = Math.min(1, THUMB_PX / Math.max(img.naturalWidth, img.naturalHeight, 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d canvas");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  paintGuideGrey(pixels.data);
  ctx.putImageData(pixels, 0, 0);
  return canvas.toDataURL("image/png");
}

/** Full-size results decoded at one time: an index of 50 rows must not decode 50 at once. */
export const MAX_DECODES = 3;
let decoding = 0;
const waiting: (() => void)[] = [];

/** The image at `url` as a small `data:` thumbnail with the guide painted grey. */
export async function renderGreyThumb(url: string): Promise<string> {
  if (decoding >= MAX_DECODES) await new Promise<void>((next) => waiting.push(next));
  else decoding++;
  try {
    return await decodeGreyThumb(url);
  } finally {
    // The place goes straight to the next one waiting, so the count never passes the limit.
    const next = waiting.shift();
    if (next) next();
    else decoding--;
  }
}

function decodeGreyThumb(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Without it the canvas is tainted by a cross-origin image and cannot be read back.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        resolve(greyDataUrl(img));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("The thumbnail image did not load."));
    img.src = url;
  });
}
